import {BaseComparator, Result, ResultState} from './comparators/BaseComparator';
import {
    DetailedDisplayProperties,
    DisplayProperties,
    DisplayPropertyType,
    GetBackgroundStyle
} from './displayproperties/DisplayProperties';
import {DisplayType} from './DisplayType';
import {GameOption} from '../game/Game';
import {SchemaConverter} from './SchemaConverter';
import {DateUtils} from '../game/DateUtils';
import {ReactElement} from 'react';

export interface SchemaValue {
    type: DisplayType,
    comparator: BaseComparator<any>
    columnDetails: ColumnDetails
}

export interface ColumnDetails {
    title: string,
    detailedTitle: string,
    message: string | ReactElement,
    width: number,
}

interface GuessProperties {
    result: Result,
    displayProperty: DisplayProperties
}

export class Guess<T> {
    constructor(
        private option: T,
        private properties: Map<string, GuessProperties>
    ) {

    }

    getOption(): T {
        return this.option;
    }

    getDisplayPropertyForKey(key: string): DisplayProperties {
        return this.properties.get(key)!.displayProperty;
    }

    getResultForKey(key: string): Result {
        return this.properties.get(key)!.result;
    }

    isMatch(): boolean {
        return Array.from(this.properties.values()).map(info => info.result.state === ResultState.MATCH).reduce((previousValue, currentValue) => previousValue && currentValue);
    }
}

export enum GameStatus {
    InProgress,
    Success,
    Fail
}

const FAR = "⬛";
const CLOSE = "🟨";
const MATCH = "🟩";

export abstract class GameContext<T> {
    private readonly actual: T;
    private readonly gameNumber: number;

    protected constructor(
        startTimestamp: Date,
        private primaryKey: string,
        public labelKey: string,
        private schemaValues: Map<string, SchemaValue>,
        private columnOrder: string[],
        public title: string,
        public totalGuesses: number,
        private optionImageUrl: string,
        protected options: T[],
        protected dailyGames: any[],
        protected helpMessage: any,
        protected primaryColor: string,
        protected secondaryColor: string = "#FFFFFF",
        public showSilhouette: boolean = true
    ) {
        const dailyGameIndex = DateUtils.DaysAgo(startTimestamp, new Date());
        const dailyGamePlayerId = dailyGames[dailyGameIndex];
        if (dailyGameIndex === undefined) {
            console.log("we are out of games lol");
            throw Error;
        }
        this.gameNumber = dailyGameIndex + 1;
        console.log(`Daily game #${this.gameNumber}`);
        this.actual = options.find(value => GameContext.GetValueOfOption(value, this.primaryKey) === dailyGamePlayerId)!;
    }

    getGameNumber(): number {
        return this.gameNumber;
    }

    getPrimaryColor(): string {
        return this.primaryColor;
    }

    getSecondaryColor(): string {
        return this.secondaryColor;
    }

    getHelpContent(): any {
        return this.helpMessage;
    }

    getState(guesses: Guess<T>[]): GameStatus {
        if (guesses[guesses.length - 1]?.isMatch()) {
            return GameStatus.Success;
        }
        if (guesses.length === this.totalGuesses) {
            return GameStatus.Fail;
        }
        return GameStatus.InProgress;
    }

    getActualResultInformation(): [string, string] {
        const id = GameContext.GetValueOfOption(this.actual, this.primaryKey);
        const label = GameContext.GetValueOfOption(this.actual, this.labelKey) as string;
        return [label, this.optionImageUrl.replace("%d", id).replace("%s", id)];
    }

    getActualDisplayProperties(): DisplayProperties[][] {
        const guess = this.processGuess(this.actual, 0, true);
        return this.getGuessDisplayProperties([guess], true);
    }

    getOptions(alreadyGuessed: T[]): GameOption<T>[] {
        return this.options.filter(option => {
            const optionId = GameContext.GetValueOfOption(option, "id");
            return !alreadyGuessed.map(guess => GameContext.GetValueOfOption(guess, "id")).includes(optionId);
        }).map(option => {
                const label = GameContext.GetValueOfOption(option, this.labelKey);
                return {
                    value: option,
                    label: label
                };
            }
        );
    }

    getColumnDetails(forResult: boolean = false): ColumnDetails[] {
        return this.getColumnOrder(forResult).map(columnKey => this.schemaValues.get(columnKey)!.columnDetails);
    }

    getColumnTitles(forResult: boolean = false): string[] {
        return this.getColumnOrder(forResult).map(columnKey => this.schemaValues.get(columnKey)!.columnDetails.title);
    }

    getColumnWidths(forResult: boolean = false): number[] {
        const columnWidths = this.getColumnOrder(forResult).map(columnKey => this.schemaValues.get(columnKey)!.columnDetails.width);
        const columnWidthSum = columnWidths.reduce((prev, curr) => prev + curr);
        return columnWidths.map(width => Math.floor(width / columnWidthSum * 100));
    }

    processGuess(guess: T, count: number, forResult: boolean = false): Guess<T> {
        const properties = new Map<string, GuessProperties>();
        const id = GameContext.GetValueOfOption(guess, "id") as number;
        let isMatch = true;
        Object.keys(guess).filter(key => (key !== this.primaryKey))
            .forEach((key) => {
                const guessValue = GameContext.GetValueOfOption(guess, key);
                const actualValue = GameContext.GetValueOfOption(this.actual, key);
                let result = this.schemaValues.get(key)!.comparator.compare(guessValue, actualValue);
                result = !forResult ? result : {
                    state: ResultState.FAR
                };
                isMatch = isMatch && result.state === ResultState.MATCH;
                properties.set(key, {
                    result: result,
                    displayProperty: this.getDisplayProperties(id, result, key, guessValue)
                });
            });
        // Make all red if failed last guess
        if (count === this.totalGuesses && !isMatch) {
            Object.keys(guess).filter(key => key !== this.primaryKey).forEach(key => {
                let property = properties.get(key)!;
                const guessValue = GameContext.GetValueOfOption(guess, key);
                property.result.state = ResultState.FAIL;
                property.result.direction = undefined;
                properties.delete(key);
                properties.set(key, {
                    result: property.result,
                    displayProperty: this.getDisplayProperties(id, property.result, key, guessValue)
                });
            });
        }
        return new Guess(guess, properties);
    }

    getResultMessage(guesses: Guess<T>[]): string {
        const letter = this.getState(guesses) === GameStatus.Fail ? "X" : guesses.length;
        let result = `${this.title} #${this.gameNumber} - ${letter}/${this.totalGuesses}\n`;
        result += this.getGuessDisplayResult(guesses, true).map(guess => {
            return guess.map(cell => {
                switch (cell.state) {
                    case ResultState.MATCH:
                        return MATCH;
                    case ResultState.CLOSE:
                        return CLOSE;
                    default:
                    case ResultState.FAR:
                        return FAR;
                }
            }).join("");
        }).join("\n");
        return result;
    }

    getGuessDisplayProperties(guesses: Guess<T>[], forResult: boolean = false): DisplayProperties[][] {
        return guesses.map(guess => {
            return this.getColumnOrder(forResult).map(columnKey => guess.getDisplayPropertyForKey(columnKey));
        });
    }

    getGuessDisplayResult(guesses: Guess<T>[], forResult: boolean = false): Result[][] {
        return guesses.map(guess => {
            return this.columnOrder.filter(columnKey => {
                return !(forResult && columnKey === this.labelKey);
            }).map(columnKey => guess.getResultForKey(columnKey));
        });
    }

    private getColumnOrder(forResultMessage: boolean): string[] {
        return this.columnOrder.filter(columnKey => {
            return !(forResultMessage && columnKey === this.labelKey);
        });
    }

    protected verifySchema() {
        // TODO: verify that everything passed in is legit
    }

    protected getImageDetails(value: any, key: string): [string[], string[]] {
        switch (key) {
            case "nationality":
                return [[`https://flagcdn.com/${(value as string).toLowerCase()}.svg`], [value]];
            default:
                return [[""], [""]];
        }
    }

    private getDisplayProperties(id: number, result: Result, key: string, value: any): DisplayProperties {
        const type = this.schemaValues.get(key)!.type;
        let exactProperties: DetailedDisplayProperties;
        let displayPropertyType: DisplayPropertyType;
        const style = GetBackgroundStyle(result.state);
        switch (type) {
            case DisplayType.IMAGE:
            case DisplayType.IMAGE_WITH_BORDER: {
                const details = this.getImageDetails(value, key);
                exactProperties = {
                    url: details[0][0],
                    description: details[1][0],
                    border: type === DisplayType.IMAGE_WITH_BORDER
                };
                displayPropertyType = DisplayPropertyType.IMAGE;
                break;
            }
            case DisplayType.MULTI_IMAGE: { // TODO: Maybe clean up a bit
                const details = this.getImageDetails(value, key);
                exactProperties = {
                    url1: details[0][0],
                    description1: details[1][0],
                    url2: details[0][1],
                    description2: details[1][1]
                };
                displayPropertyType = DisplayPropertyType.MULTI_IMAGE;
                break;
            }
            case DisplayType.HEIGHT: {
                exactProperties = {
                    content: SchemaConverter.convertInchesToFeet(value as number),
                    bold: false,
                    color: style[1]
                };
                displayPropertyType = DisplayPropertyType.TEXT;
                break;
            }
            case DisplayType.TEXT: {
                const bold = key === this.labelKey;
                exactProperties = {
                    content: value as string,
                    bold: bold,
                    color: style[1]
                };
                displayPropertyType = DisplayPropertyType.TEXT;
                break;
            }
            case DisplayType.TEXT_ARRAY: {
                exactProperties = {
                    content: (value as string[]).join(', '),
                    bold: false,
                    color: style[1]
                };
                displayPropertyType = DisplayPropertyType.TEXT;
                break;
            }
            case DisplayType.DATE: {
                const date = new Date(value as number * 1000);
                exactProperties = {
                    content: date.toLocaleDateString(),
                    bold: false,
                    color: style[1]
                };
                displayPropertyType = DisplayPropertyType.TEXT;
                break;
            }
        }
        return {
            key: `${id}_${key}_${value}`,
            state: result.state,
            direction: result.direction,
            type: displayPropertyType,
            detailedProperties: exactProperties,
            background: style[0]
        };
    }

    private static GetValueOfOption<T>(option: T, key: string): any {
        return option[key as keyof T] as any;
    }
}
